import { useEffect, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { ArrowRight, CalendarDays, Repeat, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { DateField } from "@/components/form/DateField";
import {
  formatIsoDateForDisplay,
  normalizeDateForPayload,
} from "@/lib/format";
import {
  WEEKDAY_LABELS,
  formatRecurringDate,
  generateRecurringDates,
  repeatPatternKey,
} from "@/lib/recurrence";
import { MAX_RECURRING_OCCURRENCES } from "@mtform/shared";
import {
  DuplicateDateWarning,
  useTripDateCollisions,
} from "../DuplicateDateGate";
import { WizardCard } from "../WizardCard";
import type { TripFormValues } from "../schema";
import type { WizardStepStatus } from "../WizardCard";
import { cn } from "@/lib/utils";

interface TransportDateStepProps {
  index: number;
  status: WizardStepStatus;
  onContinue: () => void;
  onEdit: () => void;
  /** Duplicate-acknowledgement state, owned by TripForm so this step and the
   *  review step share one answer — see DuplicateDateGate. */
  acknowledgedKey?: string;
  onAcknowledge?: (key: string) => void;
}

const WEEK_OPTIONS = [1, 2, 3, 4, 5, 6, 8, 10, 12] as const;

export function TransportDateStep({
  index,
  status,
  onContinue,
  onEdit,
  acknowledgedKey = "",
  onAcknowledge,
}: TransportDateStepProps) {
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
    trigger,
  } = useFormContext<TripFormValues>();
  const value = useWatch({ control, name: "transportDate" }) ?? "";
  const repeatEnabled = useWatch({ control, name: "repeatEnabled" }) ?? false;
  const weekdays = useWatch({ control, name: "repeatWeekdays" }) ?? [];
  const weeks = useWatch({ control, name: "repeatWeeks" }) ?? 4;
  const dates = useWatch({ control, name: "repeatDates" }) ?? [];

  const startIso = normalizeDateForPayload(value);

  // Same-date duplicate check. The lookup step cannot do this — no date has
  // been chosen yet at that point — so it happens here, the moment the booker
  // commits to a day. Recurring series are checked occurrence by occurrence.
  const datesBeingBooked = useMemo(
    () => (repeatEnabled ? dates : startIso ? [startIso] : []),
    [repeatEnabled, dates, startIso],
  );
  const { collisions, collisionKey } = useTripDateCollisions(datesBeingBooked);
  const acknowledged = collisionKey !== "" && acknowledgedKey === collisionKey;

  // Regenerate the date list whenever the pattern inputs change (start
  // date, weekdays, weeks). A per-date removal in the preview edits
  // `repeatDates` directly; changing the *pattern* deliberately resets
  // the list (you picked a new pattern, so a fresh generation is what
  // you want). The form remembers the pattern the list was built from, so
  // a restored draft, whose list was built from the pattern it brings,
  // keeps the dates the booker took out.
  useEffect(() => {
    if (!repeatEnabled) return;
    const pattern = repeatPatternKey(startIso, weekdays, weeks);
    if (getValues("repeatBuiltFrom") === pattern) return;
    const generated = generateRecurringDates({
      startIso,
      weekdays,
      weeks,
    });
    setValue("repeatDates", generated, { shouldDirty: true });
    setValue("repeatBuiltFrom", pattern, { shouldDirty: true });
    // We intentionally key only on the pattern inputs — not on
    // `dates` — so user removals aren't immediately re-added.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatEnabled, startIso, JSON.stringify(weekdays), weeks]);

  function toggleRepeat(next: boolean) {
    setValue("repeatEnabled", next, { shouldDirty: true });
    // Turned back on, the list is built afresh, as it always was.
    if (!next) setValue("repeatBuiltFrom", "", { shouldDirty: true });
    if (next && weekdays.length === 0 && startIso) {
      // Seed with the start date's own weekday so enabling "repeat"
      // immediately produces a sensible single-day-a-week pattern.
      const d = new Date(`${startIso}T00:00:00Z`);
      if (!Number.isNaN(d.getTime())) {
        setValue("repeatWeekdays", [d.getUTCDay()], { shouldDirty: true });
      }
    }
  }

  function toggleWeekday(day: number) {
    const set = new Set(weekdays);
    if (set.has(day)) set.delete(day);
    else set.add(day);
    setValue("repeatWeekdays", [...set], { shouldDirty: true });
  }

  function removeDate(iso: string) {
    setValue(
      "repeatDates",
      dates.filter((d) => d !== iso),
      { shouldDirty: true },
    );
  }

  async function handleContinue() {
    const fields: Array<keyof TripFormValues> = ["transportDate"];
    if (repeatEnabled) fields.push("repeatDates");
    const ok = await trigger(fields);
    if (!ok) return;
    onContinue();
  }

  if (status === "complete") {
    const iso = normalizeDateForPayload(value);
    const summary =
      repeatEnabled && dates.length > 0
        ? `${dates.length} trip${dates.length === 1 ? "" : "s"} · ${formatRecurringDate(dates[0]!)} → ${formatRecurringDate(dates[dates.length - 1]!)}`
        : iso
          ? formatIsoDateForDisplay(iso)
          : value || "—";
    return (
      <WizardCard
        index={index}
        title="Transport date"
        status="complete"
        summary={summary}
        onEdit={onEdit}
      />
    );
  }

  const continueDisabled =
    (repeatEnabled ? dates.length === 0 : !normalizeDateForPayload(value)) ||
    (collisions.length > 0 && !acknowledged);

  return (
    <WizardCard
      index={index}
      title="Transport date"
      description="When does the patient need to be picked up?"
      status={status}
    >
      <Field
        className="max-w-xs"
        label={repeatEnabled ? "First trip date" : "Date of transport"}
        required
        htmlFor="transportDate"
        error={errors.transportDate?.message}
      >
        <Controller
          control={control}
          name="transportDate"
          render={({ field, fieldState }) => (
            <DateField
              id="transportDate"
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              invalid={Boolean(fieldState.error)}
              futureOnly
            />
          )}
        />
      </Field>

      {/* ---- Repeat this trip ------------------------------------------- */}
      <div className="rounded-2xl border border-border bg-foreground/[0.02] p-3.5">
        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            checked={repeatEnabled}
            onChange={(e) => toggleRepeat(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border border-border bg-foreground/[0.04] accent-primary"
          />
          <span className="flex-1">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <Repeat className="h-3.5 w-3.5 text-primary" />
              Repeat this trip
            </span>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              Book the same trip on multiple days at once — e.g. every
              Monday, Wednesday, and Friday for several weeks. Each day is
              its own independent trip.
            </span>
          </span>
        </label>

        {repeatEnabled ? (
          <div className="mt-3 space-y-3 border-t border-foreground/10 pt-3">
            {/* Weekday picker */}
            <div>
              <p className="mb-1.5 text-xs font-semibold text-muted-foreground">
                Repeat on
              </p>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAY_LABELS.map((d) => {
                  const active = weekdays.includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => toggleWeekday(d.value)}
                      aria-pressed={active}
                      className={cn(
                        "rounded-full border px-3 py-1 text-xs font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        active
                          ? "border-primary bg-primary/15 text-primary"
                          : "border-border bg-foreground/[0.03] text-muted-foreground hover:border-primary/40 hover:text-foreground",
                      )}
                    >
                      {d.short}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Duration */}
            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor="repeatWeeks"
                className="text-xs font-semibold text-muted-foreground"
              >
                For
              </label>
              <select
                id="repeatWeeks"
                value={weeks}
                onChange={(e) =>
                  setValue("repeatWeeks", Number(e.target.value), {
                    shouldDirty: true,
                  })
                }
                className="rounded-lg border border-border bg-foreground/[0.04] px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {WEEK_OPTIONS.map((w) => (
                  <option key={w} value={w}>
                    {w} week{w === 1 ? "" : "s"}
                  </option>
                ))}
              </select>
              <span className="text-xs text-muted-foreground">
                from the first trip date
              </span>
            </div>

            {/* Generated date preview */}
            {weekdays.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border bg-background/40 px-3 py-2 text-xs text-muted-foreground">
                Pick at least one weekday to see the dates.
              </p>
            ) : dates.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border bg-background/40 px-3 py-2 text-xs text-muted-foreground">
                No matching dates in that range. Try a later start date or
                different weekdays.
              </p>
            ) : (
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {dates.length} trip{dates.length === 1 ? "" : "s"} will be
                  booked
                  {dates.length >= MAX_RECURRING_OCCURRENCES ? (
                    <span className="font-normal text-amber-700 dark:text-amber-300">
                      · capped at {MAX_RECURRING_OCCURRENCES}
                    </span>
                  ) : null}
                </p>
                <div className="flex max-h-44 flex-wrap gap-1.5 overflow-y-auto">
                  {dates.map((iso) => (
                    <span
                      key={iso}
                      className="inline-flex items-center gap-1 rounded-full border border-primary/35 bg-primary/[0.06] py-0.5 pl-2.5 pr-1 text-xs font-semibold text-primary/90"
                    >
                      {formatRecurringDate(iso)}
                      <button
                        type="button"
                        onClick={() => removeDate(iso)}
                        aria-label={`Remove ${formatRecurringDate(iso)}`}
                        // before:-inset-2 extends the tap target well
                        // beyond the 20px visual circle without
                        // inflating the chip itself.
                        className="relative flex h-5 w-5 items-center justify-center rounded-full text-primary/70 transition before:absolute before:-inset-2 before:content-[''] hover:bg-primary/20 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                {errors.repeatDates?.message ? (
                  <p className="mt-1.5 text-xs font-semibold text-destructive">
                    {errors.repeatDates.message}
                  </p>
                ) : null}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {collisions.length > 0 ? (
        <DuplicateDateWarning
          collisions={collisions}
          acknowledged={acknowledged}
          onAcknowledge={(checked) => onAcknowledge?.(checked ? collisionKey : "")}
        />
      ) : null}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleContinue}
          disabled={continueDisabled}
          className="min-w-[10rem]"
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </WizardCard>
  );
}
